<template>
    <b-overlay
        variant="white"
        :show="showLoading"
        spinner-variant="primary"
        blur="0"
        opacity=".85"
        rounded="sm"
    >
        <RequestLifeCycle
            ref="requestLifeCycle"
            :life-cycle="request.lifeCycle"
        />
        <b-card ref="verticalStepComponent" id="vstv2">
            <b-row class="mb-1">
                <b-col md="4" class="d-flex align-items-center">
                    <span class="align-middle">
                        <StatusBadge
                            type="received"
                            :status="request.status"
                        />
                    </span>
                </b-col>
                <b-col md="8">
                    <Actions
                        :request="request"
                        :show-save="isSaveButtonVisible"
                        :is-reply-disabled="isReplyDisabled"
                        :show-reply="isReplyButtonVisible"
                        :show-forward="false"
                        :is-life-cycle-visible="request.lifeCycle.length > 0"
                        @showLifeCycle="showLifeCycle"
                        @close="close"
                        class="float-right"
                    />
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <h2 class="text-center">
                        {{ $t('Page(ReceivedRequest):Component(Text):page-label', {customer: request.customer.title, product: getProductTitle()}) }}
                    </h2>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <ViewRequest
                        :request="request"
                    />
                </b-col>
            </b-row>

            <h4 class="reply-process-heading">{{ $t('Page(ReceivedRequest):Component(Text):reply-process') }}</h4>

            <app-collapse
                accordion
                type="margin"
                class="vertical-steps-flow"
            >
                <app-collapse-item
                    :title="$t('Page(ReceivedRequest):Component(StepItem):step-1-label')"
                    :header-bg-variant="firstStepStyle.bg"
                    :header-text-variant="firstStepStyle.text"
                    :class="firstStepStyle.class"
                    is-visible
                    ref="step1"
                >
                    <ProductInformation
                        ref="ProductInformation"
                        class="mt-1"
                        :product="product"
                        :request="request"
                        :customer-product="request.customerProduct"
                        :included-substances="includedSubstances"
                        @characteristicsChange="characteristicsChanged"
                        @validityChanged="(isValid) => changeStepValidity(1, isValid)"
                        @productTypeChanged="onProductTypeChange"
                        @isComplex="isComplexChange"
                        @change="saveProduct"
                        @changeSupplier="manageSupplierChange"
                    />
                    <div class="text-center">
                        <DoneGoToNextStep
                            class="mt-1"
                            :disabled="!isStepValid(1)"
                            @click="() => doneStep(1)"
                        />
                    </div>
                </app-collapse-item>
                <app-collapse-item
                    :title="$t('Page(ReceivedRequest):Component(StepItem):step-2-label')"
                    :disabled="isStep2Disabled"
                    :class="getStepVariantStyles(2).class"
                    :header-bg-variant="getStepVariantStyles(2).bg"
                    :header-text-variant="getStepVariantStyles(2).text"
                    ref="step2"
                >
                    <ProductComponentsDefinition
                        class="mt-1"
                        :product="product"
                        :request="request"
                        @validityChanged="(isValid) => changeStepValidity(2, isValid)"
                        @updateComponent="updateComponent"
                        @componentIncluded="onComponentIncluded"
                        @refreshComponents="onHierarchyChange"
                    />
                    <div class="text-center">
                        <DoneGoToNextStep
                            class="mt-1"
                            :disabled="!isStepValid(2)"
                            @click="() => doneStep(2)"
                        />
                    </div>
                </app-collapse-item>
                <app-collapse-item
                    :title="$t('Page(ReceivedRequest):Component(StepItem):step-3-label')"
                    :disabled="isStep3Disabled"
                    :class="getStepVariantStyles(3).class"
                    :header-bg-variant="getStepVariantStyles(3).bg"
                    :header-text-variant="getStepVariantStyles(3).text"
                    @visible="step3Activation"
                    ref="step3"
                >
                    <template #header>
                        <span class="lead collapse-title">{{ $t('Page(ReceivedRequest):Component(StepItem):step-3-label') }}</span>
                    </template>
                    <ConcernElements
                        ref="ConcernElementsComponent"
                        :request="request"
                        :product="product"
                        :compact-view="concernElementsCompactView"
                        :included-substances="includedSubstances"
                        :excluded-substances="excludedSubstances"
                        :components-concern-elements="componentsConcernElements"
                        :show-product-weight="showProductWeight && isProductWeightRequired"
                        :show-product-weight-message="showProductWeightMessage"
                        @validityChanged="(isValid) => changeStepValidity(3, isValid)"
                        @forward="forward"
                        @undefined="makeUndefined"
                        @include="includeSubstance"
                        @exclude="excludeSubstance"
                        @excludeModal="excludeModal"
                        @substanceChange="productPropertiesChanged"
                        @informationOwnerChange="informationOwnerChange"
                        @isComplex="isComplexChange"
                        @hasConcernSubstances="onHasConcernSubstanceChange"
                        @listQuestionStateUpdated="listQuestionStateUpdated"
                        @modalClosed="onConcernElementsModalSelectorClose"
                        @componentsModalClosed="onComponentsConcernElementsModalClosed"
                        @componentSubstanceIncluded="componentSubstanceIncluded"
                        @componentSubstanceExcluded="componentSubstanceExcluded"
                        @componentSubstanceExcludedAndSaved="componentSubstanceExcludedAndSaved"
                        @characteristicsChange="characteristicsChanged"
                    />
                    <div class="text-center">
                        <DoneGoToNextStep
                            class="mt-1"
                            :disabled="!isStepValid(3)"
                            @click="() => doneStep(3)"
                        />
                    </div>
                </app-collapse-item>
                <app-collapse-item
                    :title="$t('Page(ReceivedRequest):Component(StepItem):step-4-label')"
                    :disabled="!isScipInformationStepActive"
                    :class="getStepVariantStyles(4).class"
                    :header-bg-variant="getStepVariantStyles(4).bg"
                    :header-text-variant="getStepVariantStyles(4).text"
                    ref="step4"
                >
                    <InstructionText>
                        <span v-html="$t('Page(ReceivedRequest):Component(Instruction):scip-information')"/>
                    </InstructionText>
                    <ScipInformation
                        :product="product"
                        :component-containers="componentsConcernElements"
                        @validityChanged="(isValid) => changeStepValidity(4, isValid)"
                        @input="manageScipChange"
                        @componentScipInformationChange="componentScipInformationChange"
                    />
                    <div class="text-center">
                        <DoneGoToNextStep
                            class="mt-1"
                            :disabled="!isStepValid(4)"
                            @click="() => doneStep(4)"
                        />
                    </div>
                </app-collapse-item>
<!--                <app-collapse-item-->
<!--                    :title="$t('Page(ReceivedRequest):Component(StepItem):step-5-label')"-->
<!--                    :disabled="!isManufacturingStepActive"-->
<!--                    :class="getStepVariantStyles(5).class"-->
<!--                    :header-bg-variant="getStepVariantStyles(5).bg"-->
<!--                    :header-text-variant="getStepVariantStyles(5).text"-->
<!--                    ref="step5"-->
<!--                >-->
<!--                    <InstructionText>-->
<!--                        <span v-html="$t('Page(ReceivedRequest):Component(Instruction):manufacturing-origin')"/>-->
<!--                    </InstructionText>-->
<!--                    <ManufacturingOriginDeclaration-->
<!--                        :product="product"-->
<!--                        :components="manufacturingOriginComponents"-->
<!--                        @validityChanged="(isValid) => changeStepValidity(5, isValid)"-->
<!--                    />-->
<!--                    <div class="text-center">-->
<!--                        <DoneGoToNextStep-->
<!--                            class="mt-1"-->
<!--                            :disabled="!isStepValid(5)"-->
<!--                            @click="() => doneStep(5)"-->
<!--                        />-->
<!--                    </div>-->
<!--                </app-collapse-item>-->
                <app-collapse-item
                    :title="$t('Page(ReceivedRequest):Component(StepItem):step-6-label')"
                    :disabled="!isCustomCodesStepActive"
                    :class="getStepVariantStyles(6).class"
                    :header-bg-variant="getStepVariantStyles(6).bg"
                    :header-text-variant="getStepVariantStyles(6).text"
                    ref="step6"
                >
                    <InstructionText>
                        <span v-html="$t('Page(ReceivedRequest):Component(Instruction):custom-codes')"/>
                    </InstructionText>
                    <CustomCodes
                        ref="customCodes"
                        :product="product"
                        :components="customCodesComponents"
                        @validityChanged="(isValid) => changeStepValidity(6, isValid)"
                    />
                    <div class="text-center">
                        <DoneGoToNextStep
                            class="mt-1"
                            :disabled="!isStepValid(6)"
                            @click="() => doneStep(6)"
                        />
                    </div>
                </app-collapse-item>
                <app-collapse-item
                    :title="$t('Page(ReceivedRequest):Component(StepItem):step-7-label')"
                    :class="getStepVariantStyles(7).class"
                    :header-bg-variant="getStepVariantStyles(7).bg"
                    :header-text-variant="getStepVariantStyles(7).text"
                    ref="step7"
                >
                    <InstructionText>
                        {{ $t('Page(ReceivedRequest):Component(Instruction):verification-documents') }}
                    </InstructionText>
                    <Verification
                        ref="verification"
                        :product="product"
                        :id="productId"
                        :compact-view="verificationCompactView"
                        :doc="declarationsOfConformity"
                        :evidence="evidenceReports"
                        :compliant-statement="compliantStatement"
                        :safe-use="safeUseInstructions"
                        :disassembling="disassemblingInstructions"
                        :is-safe-use-applicable="safeUseApplicable"
                        :doc-border-variant="docStyle.border"
                        :compliant-statement-border-variant="compliantStatementStyle.border"
                        :evidence-report-border-variant="evidenceReportStyle.border"
                        :safe-user-border-variant="safeUseState.border"
                        :dissembling-border-variant="dissemblingState.border"
                        :manage-predefined="false"
                        :requested-legislations="request.legislations"
                        :default-compliant-statement-legislation-list="requestedLegislationIds"
                        :default-evidence-report-legislation-list="requestedLegislationIds"
                        @safeUseApplicableChange="verificationSafeUseChange"
                        @addDoc="addDoC"
                        @removeDoc="removeDeclarationOfConformity"
                        @docChange="onDocumentChange"
                        @addCompliantStatement="addCompliantStatement"
                        @removeCompliantStatement="removeCompliantStatement"
                        @compliantStatementChange="onDocumentChange"
                        @evidenceReportChange="onDocumentChange"
                        @addReport="addReport"
                        @removeReport="removeEvidenceReport"
                        @safeUseChange="safeUseDocumentChange"
                        @disassemblingChange="dissemblingDocumentChange"
                    />
                    <div class="text-center">
                        <DoneGoToNextStep
                            class="mt-1"
                            :disabled="!isVerificationStepFullFiled"
                            @click="() => doneStep(7)"
                        />
                    </div>
                </app-collapse-item>
                <app-collapse-item
                    :title="$t('Page(ReceivedRequest):Component(StepItem):step-8-label')"
                    :class="getStepVariantStyles(8).class"
                    :header-bg-variant="getStepVariantStyles(8).bg"
                    :header-text-variant="getStepVariantStyles(8).text"
                    ref="step8"
                >
                    <Overlay :show="otherReplyInformationLoading">
                        <InstructionText>
                            <span v-html="$t('Page(ReceivedRequest):Component(Instruction):other-information')"/>
                        </InstructionText>

                        <OtherReplyInformation
                            :request="request"
                            @validityChange="(isValid) => changeStepValidity(8, isValid)"
                            @input="onOtherInformationChange"
                        />

                        <div class="text-center">
                            <DoneGoToNextStep
                                class="mt-1"
                                :disabled="!isStepValid(8)"
                                @click="completeOtherInformation"
                            />
                        </div>
                    </Overlay>
                </app-collapse-item>
                <app-collapse-item
                    :title="$t('Page(ReceivedRequest):Component(StepItem):step-9-label')"
                    v-if="isApproveDeclarationStepVisible"
                    :disabled="isApproveDeclarationStepDisabled"
                    :class="getStepVariantStyles(9).class"
                    :header-bg-variant="getStepVariantStyles(9).bg"
                    :header-text-variant="getStepVariantStyles(9).text"
                    ref="step9"
                >
                    <InstructionText>
                        <span v-html="$t('Page(ReceivedRequest):Component(Instruction):approve-declaration')"/>
                    </InstructionText>
                    <DeclarationApproval
                        class="no-border"
                        label=""
                        :request-id="id"
                        @confirmed="declarationApproved"
                    />
                </app-collapse-item>
                <app-collapse-item
                    v-if="isBulkReplyCandidatesStepVisible"
                    :disabled="!isReplyStepActive"
                    :title="$t('Page(ReceivedRequest):Component(SimilarReplyRequestSelection):step-label')"
                    ref="stepBulkReplyCandidates"
                >
                    <SimilarReplyRequestSelection
                        :requests="bulkReplyCandidates"
                        :selected-request-ids="selectedBulkReplyRequestIds"
                        :error="bulkReplyCandidatesError"
                        @update:selected-request-ids="updateSelectedBulkReplyRequestIds"
                        @retry="retryBulkReplyCandidatesAfterOtherInformation"
                    />
                    <div v-if="hasBulkReplyCandidates" class="text-center">
                        <DoneGoToNextStep class="mt-1" @click="completeBulkReplyCandidatesStep" />
                    </div>
                </app-collapse-item>
                <app-collapse-item
                    :disabled="!isReplyStepActive"
                    :title="$t('Page(ReceivedRequest):Component(StepItem):step-final')"
                    ref="stepSendReply"
                >
                    <SendReply
                        :reply-details="request.replyDetails"
                        @send="sendReply"
                    />
                </app-collapse-item>
            </app-collapse>
        </b-card>
    </b-overlay>
</template>

<script>
import { BCard, BRow, BCol, BOverlay} from 'bootstrap-vue'
import {Actions} from "@/views/request/received/components";
import StatusBadge from "@/views/request/components/StatusBadge";
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import {ProductCardMixins} from "@/views/product/components/product-card";
import {ProductType, VerificationDocumentType} from "@/libs/product";
import DeclarationApproval from "@/views/request/received/components/DeclarationApproval";
import {
    ConcernElements, ViewRequest, ProductInformation, ScipInformation,
    ProductComponentsDefinition, ManufacturingOriginDeclaration
} from "@/views/request/received/flow/components/vertiacal-steps";
import {Verification} from "@/views/product/components/product-card";
import OtherReplyInformation from "@/views/request/received/components/send-reply/OtherReplyInformation";
import SendReply from "@/views/request/received/flow/components/vertiacal-steps/SendReply";
import verticalStepsMixin from "@/views/request/received/flow/vertical-steps-mixin";
import DoneGoToNextStep from "@/views/request/received/flow/components/vertiacal-steps/DoneGoToNextStep";
import CustomCodes from "@/views/request/received/flow/components/vertiacal-steps/CustomCodes";
import {ProductProvider} from "@core/domain/product";
import InstructionText from "@core/components/common/instructions/InstructionText.vue";
import Overlay from "@core/components/common/Overlay.vue";
import RequestLifeCycle from "@/views/request/components/RequestLifeCycle.vue";
import SimilarReplyRequestSelection from "@/views/request/received/components/send-reply/SimilarReplyRequestSelection.vue";
import bulkReplyCandidatesMixin from "@/views/request/received/flow/mixins/bulk-reply-candidates-mixin";
import bulkReplyMixin from "@/views/request/received/flow/mixins/bulk-reply-mixin";

export default {
    mixins: [verticalStepsMixin, bulkReplyCandidatesMixin, bulkReplyMixin],
    components: {
        RequestLifeCycle,
        Overlay,
        InstructionText,
        BCard, BRow, BCol, Actions, ViewRequest, ProductInformation, ScipInformation, Verification,
        BOverlay, AppCollapse, AppCollapseItem, StatusBadge, ConcernElements,
        OtherReplyInformation, DeclarationApproval, SendReply, ProductComponentsDefinition, DoneGoToNextStep,
        CustomCodes, ManufacturingOriginDeclaration, SimilarReplyRequestSelection,
    },
    props: {
        id: {
            required: true,
        }
    },
    data() {
        return {
            validSteps: [],
            doneClickedSteps: [],

            otherInformation: null,
            isReplyDisabled: true,
            isReplyButtonVisible: false,
            isSaveButtonVisible: false,
            productId: null,

            verificationCompactView: false,
            concernElementsCompactView: false,

            resizeObserver: null,

            customCodesComponents: [],
            manufacturingOriginComponents: [],

            ...ProductCardMixins.data,
        };
    },
    methods: {
        showLifeCycle() {
            this.$refs.requestLifeCycle.show();
        },
        makeCustomCodesAndManufacturingOriginComponents() {
            const included = {};
            const components = [];
            this.componentsConcernElements
                .filter(c => c.substances.length > 0)
                .forEach(container => {
                    for (let i = 1; i < container.parents.length; i++) {
                        if (!included.hasOwnProperty(container.parents[i].id)) {
                            let parents = [];
                            for (let k = 0; k < i; ++k) {
                                parents.push(container.parents[k]);
                            }

                            if (container.component.id === container.parents[i].id) {
                                container.component.parentTitle = parents.map(p => p.title).join(' / ');
                                components.push(container.component);
                            } else {
                                container.parents[i].parentTitle = parents.map(p => p.title).join(' / ');
                                components.push(container.parents[i]);
                            }
                            included[container.parents[i].id] = true;
                        }
                    }

                    if (!included.hasOwnProperty(container.component.id)) {
                        container.component.parentTitle = container.parents.map(p => p.title).join(' / ');
                        components.push(container.component);
                        included[container.component.id] = true;
                    }
                })
            ;
            this.customCodesComponents = components;
            this.manufacturingOriginComponents = components;
        },

        componentScipInformationChange(component, information) {
            this.makeCustomCodesAndManufacturingOriginComponents();
        },

        onComponentsConcernElementsModalClosed() {
            this.makeCustomCodesAndManufacturingOriginComponents();
            this.recalculateShowProductWeightMessage();
        },

        componentSubstanceExcluded(componentContainer) {
            if (componentContainer.substances.length === 0) {
                this.makeCustomCodesAndManufacturingOriginComponents();
            }
        },

        componentSubstanceExcludedAndSaved() {
            this.recalculateShowProductWeightMessage();
        },

        componentSubstanceIncluded(componentContainer) {
            //  we have dependency on categories for custom codes for all components that contain concern elements
            //  it means we have to take care we show component's custom codes
            //  there is a case when component's custom codes were not loaded:
            //  - when component has not had a concern elements
            if (componentContainer.component.categories.length === 0) {
                const component = ProductProvider(componentContainer.component.id);
                if (!component.isInited()) {
                    component
                        .getCard()
                        .then(card => {
                            componentContainer.component.categories = card.categories;
                        });
                } else if (componentContainer.component.categories.length !== component.card.categories.length) {
                    componentContainer.component.categories = component.card.categories;
                }
            }
        },

        removeDeclarationOfConformity(document, index) {
            this.declarationsOfConformity.splice(index, 1);
        },

        removeEvidenceReport(document, index) {
            this.evidenceReports.splice(index, 1);
        },

        close() {
            this.$router.push({
                name: 'received-requests',
            });
        },

        getProductTypeTranslationKey() {
            switch (this.product.type) {
                case ProductType.GROUP:
                    return 'Component(Text):product-type-group'

                case ProductType.OWN:
                    return 'Component(Text):product-type-own'

                case ProductType.PURCHASED:
                    return 'Component(Text):product-type-purchased'

                default:
                    return '';
            }
        },

        getProductTitle() {
            if (this.product.names.length > 0) {
                return this.product.names[0].value;
            }

            return '';
        },

        ...ProductCardMixins.methods,

        getProductId() {
            return this.request ? this.request.product.id : null;
        },

        changeStepValidity(step, isValid) {
            if (isValid && !this.validSteps.includes(step)) {
                this.validSteps.push(step);
            } else if (!isValid && this.validSteps.includes(step)) {
                this.validSteps.splice(this.validSteps.findIndex(s => s === step), 1);
            }
        },

        step3Activation() {
            this.$refs.ConcernElementsComponent.stepActivation();
        },

        declarationApproved() {
            if (this.$refs.step9) {
                this.$refs.step9.updateVisible(false);
            }

            this.isDeclarationApproved = true;
            this.changeStepValidity(7, true);
            this.$store.dispatch('declarationApproved');

            this.$nextTick(() => {
                if (this.hasBulkReplyCandidates && this.$refs.stepBulkReplyCandidates) {
                    this.$refs.stepBulkReplyCandidates.updateVisible(true);
                } else if (this.$refs.stepSendReply) {
                    this.$refs.stepSendReply.updateVisible(true);
                }
            });
        },

        async completeOtherInformation() {
            await this.loadBulkReplyCandidates();
            this.markStepAsDone(8);

            if (this.isBulkReplyCandidatesStepReachable) {
                this.$nextTick(() => this.$refs.stepBulkReplyCandidates.updateVisible());
                return;
            }

            this.gotoNextStep(9);
        },

        async retryBulkReplyCandidatesAfterOtherInformation() {
            await this.loadBulkReplyCandidates({ force: true });

            if (this.bulkReplyCandidatesError) {
                return;
            }

            if (this.isBulkReplyCandidatesStepReachable) {
                this.$nextTick(() => this.$refs.stepBulkReplyCandidates.updateVisible());
                return;
            }

            this.gotoNextStep(9);
        },

        completeBulkReplyCandidatesStep() {
            this.$refs.stepSendReply.updateVisible();
        },

        gotoNextStep(step) {
            if (step === 9) {
                if (this.isApproveDeclarationStepVisible && !this.isApproveDeclarationStepDisabled) {
                    this.$refs.step9.updateVisible();
                } else {
                    this.$refs.stepSendReply.updateVisible();
                }
            } else {
                if (9 === step) {
                    this.$refs.stepSendReply.updateVisible();
                    return;
                }
                if (2 === step && this.isStep2Disabled) {
                    step += 1;
                }
                if (4 === step && !this.isCustomCodesStepActive) {
                    step += 1;
                }
                if (5 === step && !this.isManufacturingStepActive) {
                    step += 1;
                }
                if (6 === step && !this.isScipInformationStepActive) {
                    step += 1;
                }

                this.$refs['step' + step].updateVisible();
            }
        },

        doneStep(step) {
            if (!this.doneClickedSteps.includes(step)) {
                this.doneClickedSteps.push(step);
            }
            this.gotoNextStep(step + 1);
            this.markStepAsDone(step);
        },

        isStepValid(step) {
            return this.validSteps.includes(step);
        },

        getStepVariantStyles(step) {
            if (8 === step) {   // approve declaration
                return this.isStepValid(step)
                ? {bg: 'default', text: 'default', class: 'done'}
                : {bg: 'default', text: 'default', class: ''};
            }

            return this.isStepValid(step) && this.doneSteps.includes(step)
                ? {bg: 'default', text: 'default', class: 'done'}
                : {bg: 'default', text: 'default', class: ''};
        },
        onResize() {
            this.verificationCompactView = this.$refs.verticalStepComponent.offsetWidth < 1500;
            this.concernElementsCompactView = this.$refs.verticalStepComponent.offsetWidth < 1145;
        },

        managePredefinedDocuments() {
            //  HAM-510

            //  Reports
            if (this.request.verificationDocuments.includes(VerificationDocumentType.EVIDENCE_REPORT)) {
                if (this.request.evidenceReportTypes.length > 0) {
                    const requestedTypes = this.request.evidenceReportTypes.reduce((requestedTypes, type) => {
                        if (!requestedTypes.hasOwnProperty(type.id)) {
                            requestedTypes[type.id] = type;
                        }
                        return requestedTypes;
                    }, {});
                    this.evidenceReports
                        .forEach((document) => {
                            document.documentTypes.map(type => {
                                if (requestedTypes.hasOwnProperty(type.id)) {
                                    delete requestedTypes[type.id];
                                }
                            });
                        })
                    ;

                    Object.keys(requestedTypes).forEach((key) => {
                        const report = this.$refs.verification.makeEvidenceReport();
                        report.documentTypes.push(requestedTypes[key]);
                        this.addReport(report);
                    });
                } else if (this.evidenceReports.length === 0) {
                    this.addReport(this.$refs.verification.makeEvidenceReport());
                }
            }

            if (
                this.request.verificationDocuments.includes(VerificationDocumentType.DECLARATION_OR_CONFORMITY)
                && this.declarationsOfConformity.length === 0
            ) {
                this.addDoC(this.$refs.verification.makeDeclarationOfConformity());
            }

            if (
                this.request.verificationDocuments.includes(VerificationDocumentType.COMPLIANT_STATEMENT)
                && this.compliantStatement.length === 0
            ) {
                this.addCompliantStatement(this.$refs.verification.makeCompliantStatement());
            }
        }
    },
    mounted() {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.$refs.verticalStepComponent);
    },
    computed: {
        isBulkReplyCandidatesStepReachable() {
            return this.isBulkReplyCandidatesStepVisible
                && (!this.isApproveDeclarationStepVisible
                    || this.isApproveDeclarationStepDisabled);
        },
        doneSteps() {
            return this.request.doneSteps ? this.request.doneSteps : [];
        },
        isStep2Disabled() {
            if (null !== this.request.isComplexProduct) {
                return this.request.isComplexProduct === false;
            }
            return this.product.isComplex === false;
        },
        isStep3Disabled() {
            return this.product.type === ProductType.UNDEFINED;
        },
        firstStepStyle() {
            return this.getStepVariantStyles(1);
        },
        requestedLegislationIds() {
            return this.request.legislations.map(l => l.id);
        },
        isScipInformationStepActive() {
            return true === this.product.hasConcernSubstances;
        },
        isCustomCodesStepActive() {
            return true === this.product.hasConcernSubstances;
        },
        isManufacturingStepActive() {
            return false;
            // return true === this.product.hasConcernSubstances;
        },
        isVerificationStepFullFiled() {
            const verificationDocuments = this.request.verificationDocuments;
            if (verificationDocuments.length === 0) {
                return true;
            }

            let states = [];
            if (verificationDocuments.includes(VerificationDocumentType.EVIDENCE_REPORT)) {
                states.push(!(this.evidenceReports.length === 0));
                //  by default we add one empty document
                if (this.evidenceReports.length === 1 && !this.evidenceReports[0].id) {
                    states.push(false);
                }
            }

            if (verificationDocuments.includes(VerificationDocumentType.DECLARATION_OR_CONFORMITY)) {
                states.push(!(this.declarationsOfConformity.length === 0));
                //  by default we add one empty document
                if (this.declarationsOfConformity.length === 1 && !this.declarationsOfConformity[0].id) {
                    states.push(false);
                }
            }

            if (verificationDocuments.includes(VerificationDocumentType.COMPLIANT_STATEMENT)) {
                states.push(!(this.compliantStatement.length === 0));
                //  by default we add one empty document
                if (this.compliantStatement.length === 1 && !this.compliantStatement[0].id) {
                    states.push(false);
                }
            }

            if (verificationDocuments.includes(VerificationDocumentType.SAFE_USE_INSTRUCTIONS)) {
                if (false !== this.safeUseApplicable) {
                    states.push(!!(this.safeUseInstructions && (this.safeUseInstructions.file || this.safeUseInstructions.description)));
                }
            }

            if (verificationDocuments.includes(VerificationDocumentType.DISSEMBLING_INSTRUCTIONS)) {
                states.push(!!(this.disassemblingInstructions !== undefined && this.disassemblingInstructions.id));
                if (this.disassemblingInstructions && this.disassemblingInstructions.id) {
                    states.push(!!(this.disassemblingInstructions.file || this.disassemblingInstructions.description));
                }
            }

            const state = states.includes(false) === false;
            this.changeStepValidity(7, state);
            return state;
        },
        evidenceReportStyle() {
            let state = 'required';
            if (!this.request.verificationDocuments.includes(VerificationDocumentType.EVIDENCE_REPORT)) {
                state = 'default';
            } else if (this.evidenceReports.length && this.evidenceReports[0].id) {
                state = 'success';
            }
            return this.$store.state.request.received.replyVariant[state];
        },
        docStyle() {
            let state = 'required';
            if (!this.request.verificationDocuments.includes(VerificationDocumentType.DECLARATION_OR_CONFORMITY)) {
                state = 'default';
            } else if (this.declarationsOfConformity.length && this.declarationsOfConformity[0].id) {
                state = 'success';
            }
            return this.$store.state.request.received.replyVariant[state];
        },
        compliantStatementStyle() {
            let state = 'required';
            if (!this.request.verificationDocuments.includes(VerificationDocumentType.COMPLIANT_STATEMENT)) {
                state = 'default';
            } else if (this.compliantStatement.length && this.compliantStatement[0].id) {
                state = 'success';
            }
            return this.$store.state.request.received.replyVariant[state];
        },
        safeUseState() {
            if (!this.request.verificationDocuments.includes(VerificationDocumentType.SAFE_USE_INSTRUCTIONS)) {
                return this.$store.state.request.received.replyVariant.default;
            }
            if (false === this.safeUseApplicable) {
                return this.$store.state.request.received.replyVariant.success;
            }
            let state = 'required';
            if (this.safeUseInstructions && this.safeUseInstructions.id) {
                state = (this.safeUseInstructions.file || this.safeUseInstructions.description) ? 'success' : 'required';
            }

            return this.$store.state.request.received.replyVariant[state];
        },
        dissemblingState() {
            if (!this.request.verificationDocuments.includes(VerificationDocumentType.DISSEMBLING_INSTRUCTIONS)) {
                return this.$store.state.request.received.replyVariant.default;
            }

            let state = 'required';
            if (this.disassemblingInstructions && this.disassemblingInstructions.id) {
                state = (this.disassemblingInstructions.file || this.disassemblingInstructions.description) ? 'success' : 'required';
            }
            return this.$store.state.request.received.replyVariant[state];
        },
    },
    watch: {
        evidenceReports() {
            this.managePredefinedDocuments();
        }
    }
}
</script>
<style lang="scss">
#vstv2 {
    color: black;
    .card-title, .reply-process-heading, .table, h6, label, h2, .simple-flow-instruction {
        color: black;
    }
}
.vertical-steps-flow > .card {
    border: 1px solid #d5d3da !important;
}
.vertical-steps-flow > .card.done {
    border-color: #21b764b3 !important;

    background-position: right 3rem top 1.23rem;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'%3e%3cpath fill='%2321b764b3' d='M2.3 6.73L.6 4.53c-.4-1.04.46-1.4 1.1-.8l1.1 1.4 3.4-3.8c.6-.63 1.6-.27 1.2.7l-4 4.6c-.43.5-.8.4-1.1.1z'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-size: calc(0.725em + 0.438rem) calc(0.725em + 0.438rem);
}
.collapse-margin .card.border-success {
    border-bottom: 1px solid #21b764b3 !important;
}
.collapse-margin .card.border-warning {
    border-bottom: 1px solid #ffdb3c !important;
}
.vertical-steps-flow .card {
    .component-concern-elements > .card {
        border: 1px solid #d5d3da !important;
    }

    .card.component-concern-elements.has-concern-elements:not(.collapsed) > .card-title {
        border-bottom: 1px solid #d5d3da;
    }

    .card.component-concern-elements.has-concern-elements.collapsed > .card-title {
        border-bottom: none;
        margin-bottom: 0;
    }

    .card.component-concern-elements.no-concern-elements > .card-title {
        margin-bottom: 0;
    }

    .card:not(.border-success):not(.border-warning) {
        border: 1px solid rgba(34, 41, 47, 0.125) !important;
    }

    .document-row:last-child {
        margin-bottom: 2REM;
    }
}
</style>
